// src/pages/Marketplace/markets/SpotlightPanel.tsx
import React, { useMemo, useState } from 'react';
import { ErrorBanner, Spinner, SuccessBanner } from '@/pages/Admin/shared';
import type { Env } from '@/pages/Admin/shared';
import {
  formatWeekRange,
  useSpotlightDesignations,
  weekKeySequence,
} from '@/pages/Marketplace/shared';
import type { Market, SpotlightDesignation } from '@/pages/Marketplace/shared';
import DesignateSpotlightModal from './DesignateSpotlightModal';

interface Props {
  market: Market;
  env: Env;
}

/**
 * Weeks rendered from the current week forward. Long enough to plan a couple of
 * months; anything booked past the horizon still gets a row (see `weekRows`), so
 * a far-out designation is never hidden.
 */
const HORIZON_WEEKS = 9;

interface WeekRow {
  weekStart: string;
  designation: SpotlightDesignation | null;
  isCurrentWeek: boolean;
  /**
   * Whether the write endpoint would accept a change for this week. Taken from
   * the server's `editable` flag when a designation exists, and otherwise from
   * the server's `earliest_writable_week_start` — never from a local clock.
   */
  editable: boolean;
}

function formatAudit(d: SpotlightDesignation): string {
  const when = new Date(d.updated_at).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });
  // set_by_email is nullable in the schema — a designation written by anything
  // other than the admin endpoint would have no admin attached to it.
  return `${d.set_by_email ?? 'unknown'} · ${when}`;
}

export default function SpotlightPanel({ market, env }: Props): React.ReactElement {
  const { data, loading, error, refetch } = useSpotlightDesignations(env, market.id);
  const [editing, setEditing] = useState<WeekRow | null>(null);
  const [saved, setSaved] = useState(false);

  const weekRows = useMemo<WeekRow[]>(() => {
    if (!data) return [];
    const byWeek = new Map(data.items.map((d) => [d.week_start, d]));
    // Horizon weeks plus any booked week beyond it, de-duplicated and ordered.
    const keys = Array.from(new Set([
      ...weekKeySequence(data.current_week_start, HORIZON_WEEKS),
      ...data.items.map((d) => d.week_start),
    ])).sort();

    return keys.map((weekStart) => {
      const designation = byWeek.get(weekStart) ?? null;
      return {
        weekStart,
        designation,
        isCurrentWeek: designation
          ? designation.is_current_week
          : weekStart === data.current_week_start,
        // ISO date strings compare lexicographically, so this is the same
        // boundary the server applies to the rows it did return.
        editable: designation
          ? designation.editable
          : weekStart >= data.earliest_writable_week_start,
      };
    });
  }, [data]);

  const writableWeeks = useMemo(
    () => weekRows.filter((r) => r.editable).map((r) => r.weekStart),
    [weekRows],
  );

  const handleSaved = () => { setSaved(true); refetch(); };

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-white">Spotlight Bar</h2>
        <span className="text-xs text-gray-500">
          One venue per market per week · weeks start Monday (UTC)
        </span>
      </div>

      {error && <ErrorBanner message={error} />}
      {saved && <SuccessBanner message="Spotlight designation saved." />}

      {loading && !data ? (
        <div className="p-3"><Spinner size="sm" /></div>
      ) : (
        <div className="border border-gray-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 text-gray-400 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">Week</th>
                <th className="text-left px-3 py-2">Spotlight venue</th>
                <th className="text-left px-3 py-2">Designated by</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {weekRows.map((row) => (
                <tr key={row.weekStart} className="border-t border-gray-800">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={row.editable ? '' : 'text-gray-400'}>
                      {formatWeekRange(row.weekStart)}
                    </span>
                    {row.isCurrentWeek && (
                      <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full border border-emerald-700/50 text-emerald-300 bg-emerald-950/40">
                        live now
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {row.designation ? (
                      <span className="flex items-center gap-2">
                        <span>{row.designation.business_name}</span>
                        {row.designation.business_deleted && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full border border-red-800/60 text-red-300 bg-red-950/40">
                            venue deleted — re-pick
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-gray-500 italic">None designated</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-xs">
                    {row.designation ? formatAudit(row.designation) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {row.editable ? (
                      <button
                        onClick={() => { setSaved(false); setEditing(row); }}
                        className="text-xs text-violet-300 hover:text-violet-200"
                      >
                        {row.designation ? 'Replace' : 'Designate'}
                      </button>
                    ) : (
                      <span
                        className="text-xs text-gray-500"
                        title="Past and current weeks are frozen — the weekly rotation job may already have used them."
                      >
                        Read-only
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && data && <div className="p-3"><Spinner size="sm" /></div>}
        </div>
      )}

      {editing && (
        <DesignateSpotlightModal
          market={market}
          env={env}
          writableWeeks={writableWeeks}
          initialWeek={editing.weekStart}
          currentBusinessName={editing.designation?.business_name ?? null}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
    </section>
  );
}
